import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SortMode } from '../types';
import { authFetch } from '../auth/api';
import { useReplay, REPLAY_SPEEDS } from '../hooks/useReplay';
import { useColumnSettings } from '../hooks/useColumnSettings';
import { useHighlightedTeams } from '../hooks/useHighlightedTeams';
import { usePitReservedBottom } from '../hooks/usePitReservedBottom';
import { TeamGrid } from './TeamGrid';
import { TeamStintsModal } from './TeamStintsModal';
import { RaceControlPanel } from './RaceControlPanel';
import { ColumnSettings } from './ColumnSettings';
import { PitWindow } from './PitWindow';
import { StrategyPanel } from './StrategyPanel';
import { ChartsPanel } from './ChartsPanel';
import { SectorsPanel } from './SectorsPanel';
import { DriverPlanPanel } from './DriverPlanPanel';
import { LiveStrategyPanel } from './LiveStrategyPanel';
import { useStrategy } from '../hooks/useStrategy';
import { useLiveStrategy } from '../hooks/useLiveStrategy';
import PitSidebar from './PitSidebar';
import { SessionTimer } from './SessionTimer';
import { SessionBadge } from './SessionBadge';
import { navigate } from './EventsList';
import { useAuth } from '../auth/AuthContext';
import './EventsList.css';
import './ReplayView.css';

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: 'position', label: 'Position' },
  { value: 'average', label: 'Average Lap (5)' },
  { value: 'average10', label: 'Average Lap (10)' },
  { value: 'best', label: 'Best Lap' },
  { value: 'category', label: 'Category (grouped)' },
];

function formatClock(ms: number): string {
  const total = Math.floor(Math.max(0, ms) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const ReplayView: React.FC<{ eventId: string }> = ({ eventId }) => {
  const replay = useReplay(eventId);
  const [title, setTitle] = useState<string>('');
  const [sortMode, setSortMode] = useState<SortMode>('position');
  const [searchQuery, setSearchQuery] = useState('');
  const [stintsTeamId, setStintsTeamId] = useState<string | null>(null);
  const [isRaceControlVisible, setIsRaceControlVisible] = useState(false);
  const [isPitSidebarVisible, setIsPitSidebarVisible] = useState(false);

  usePitReservedBottom(isPitSidebarVisible);
  const {
    columns,
    toggleColumn,
    resetColumns,
    getVisibleColumns,
    showAllColumns,
    hideAllOptionalColumns,
  } = useColumnSettings();
  const { highlightedTeamIds, toggleHighlight } = useHighlightedTeams();
  const [isStrategyVisible, setIsStrategyVisible] = useState(false);
  const [isChartsVisible, setIsChartsVisible] = useState(false);
  const [isSectorsVisible, setIsSectorsVisible] = useState(false);
  const [isPlanVisible, setIsPlanVisible] = useState(false);
  const [isStintPlanVisible, setIsStintPlanVisible] = useState(false);
  const { isAdmin } = useAuth();

  // The replay clock, not wall time — so projections reflect the moment being
  // reviewed rather than now.
  const replayNow = useCallback(() => replay.currentTime, [replay.currentTime]);
  const strategy = useStrategy(replay.teams, {
    rules: replay.raceRules,
    sessionEndsAt: replay.sessionEndsAt,
    ourTeamIds: highlightedTeamIds,
    now: replayNow,
  });

  // The stint plans attached to this race, followed against the replayed frames.
  // Read-only: the virtual clock is passed in, which is what switches writing off.
  const liveStrategy = useLiveStrategy({
    teams: replay.teams,
    eventId,
    sessionEndsAt: replay.sessionEndsAt,
    now: replayNow,
  });

  useEffect(() => {
    let cancelled = false;
    authFetch(`/events/${eventId}`)
      .then((res) => res.json())
      .then((event: { title?: string }) => {
        if (!cancelled) setTitle(event?.title ?? '');
      })
      .catch(() => {
        // The replay itself still works without the title.
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const penaltyCountByKart = useMemo(() => {
    const counts = new Map<string, number>();
    for (const entry of replay.raceControl) {
      if (!entry.kartNumber) continue;
      if (entry.flag !== 'penalty' && entry.flag !== 'warning') continue;
      counts.set(entry.kartNumber, (counts.get(entry.kartNumber) ?? 0) + 1);
    }
    return counts;
  }, [replay.raceControl]);

  const duration = replay.endsAt - replay.startsAt;
  const elapsed = replay.currentTime - replay.startsAt;

  const header = (
    <div className="app-header">
      <h1>Karting Live Timing</h1>
      {replay.status === 'ready' && (
        <SessionTimer
          endsAt={replay.sessionEndsAt}
          now={replay.currentTime}
        />
      )}
      <SessionBadge />
    </div>
  );

  if (replay.status !== 'ready') {
    return (
      <div className="App">
        {header}
        <div className="app-main">
          <div className="main-content">
            <div className="events-page">
              <div className="events-toolbar">
                <h2 className="events-title">Replay</h2>
                <button
                  className="events-link-button"
                  onClick={() => navigate(`/events/${eventId}`)}
                >
                  ← Back to event
                </button>
              </div>
              {replay.status === 'loading' && (
                <div className="events-loading">
                  Loading recording… {formatBytes(replay.loadedBytes)} read
                </div>
              )}
              {replay.status === 'empty' && (
                <div className="events-empty">
                  This event has no replayable data. Recordings only contain
                  timing frames from the moment the backend connected.
                </div>
              )}
              {replay.status === 'error' && (
                <div className="events-error">Error: {replay.error}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {header}
      <div className="app-main">
        <div className="main-content">
          <div className="replay-bar">
            <div className="replay-bar-row">
              <span className="replay-badge">REPLAY</span>
              {title && <span className="replay-title">{title}</span>}
              <button
                className="events-link-button"
                onClick={() => navigate(`/events/${eventId}`)}
              >
                ← Back to event
              </button>
            </div>

            <div className="replay-bar-row">
              <button
                className="replay-play"
                type="button"
                onClick={replay.togglePlay}
                title={replay.isPlaying ? 'Pause' : 'Play'}
              >
                {replay.isPlaying ? '❚❚' : '▶'}
              </button>
              <button
                className="events-link-button"
                type="button"
                onClick={replay.restart}
                title="Back to the start"
              >
                ⏮
              </button>

              <span className="replay-clock">
                {formatClock(elapsed)} / {formatClock(duration)}
              </span>

              <input
                className="replay-scrub"
                type="range"
                min={replay.startsAt}
                max={replay.endsAt}
                value={replay.currentTime}
                onChange={(e) => replay.seekTo(Number(e.target.value))}
                aria-label="Seek through the recording"
              />

              <div className="replay-speeds">
                {REPLAY_SPEEDS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`replay-speed${
                      replay.speed === s ? ' replay-speed--active' : ''
                    }`}
                    onClick={() => replay.setSpeed(s)}
                  >
                    {s}×
                  </button>
                ))}
              </div>
            </div>

            <div className="replay-bar-row">
              <div className="search-wrapper">
                <label className="sort-label" htmlFor="replay-search">
                  Search
                </label>
                <input
                  id="replay-search"
                  type="search"
                  className="team-search-input"
                  placeholder="Driver, kart, team, category…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="sort-dropdown-wrapper">
                <label className="sort-label" htmlFor="replay-sort">
                  Sort
                </label>
                <select
                  id="replay-sort"
                  className="sort-select"
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value as SortMode)}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <ColumnSettings
                columns={columns}
                onToggleColumn={toggleColumn}
                onResetColumns={resetColumns}
                onShowAll={showAllColumns}
                onHideOptional={hideAllOptionalColumns}
              />

              <button
                className="control-button"
                onClick={() => setIsPitSidebarVisible((v) => !v)}
                title="Arrange karts across the pit columns"
              >
                {isPitSidebarVisible ? 'Hide' : 'Show'} Pit Monitor
              </button>

              {isAdmin && (
                <button
                  className="control-button strategy-button"
                  onClick={() => setIsStrategyVisible((v) => !v)}
                  aria-pressed={isStrategyVisible}
                >
                  {isStrategyVisible ? 'Hide' : 'Show'} Strategy
                </button>
              )}

              {isAdmin && (
                <button
                  className="control-button charts-button"
                  onClick={() => setIsChartsVisible((v) => !v)}
                  aria-pressed={isChartsVisible}
                >
                  {isChartsVisible ? 'Hide' : 'Show'} Charts
                </button>
              )}

              {isAdmin && (
                <button
                  className="control-button sectors-button"
                  onClick={() => setIsSectorsVisible((v) => !v)}
                  aria-pressed={isSectorsVisible}
                >
                  {isSectorsVisible ? 'Hide' : 'Show'} Sectors
                </button>
              )}

              {isAdmin && (
                <button
                  className="control-button plan-button"
                  onClick={() => setIsPlanVisible((v) => !v)}
                  aria-pressed={isPlanVisible}
                >
                  {isPlanVisible ? 'Hide' : 'Show'} Driver Plan
                </button>
              )}

              <button
                className="control-button plan-button"
                onClick={() => setIsStintPlanVisible((v) => !v)}
                aria-pressed={isStintPlanVisible}
              >
                {isStintPlanVisible ? 'Hide' : 'Show'} Stint Plan
                {liveStrategy.plans.length > 0 && (
                  <span className="race-control-badge">{liveStrategy.plans.length}</span>
                )}
              </button>

              <button
                className="control-button race-control-button"
                onClick={() => setIsRaceControlVisible((v) => !v)}
              >
                {isRaceControlVisible ? 'Hide' : 'Show'} Race Control
                {replay.raceControl.length > 0 && (
                  <span className="race-control-badge">
                    {replay.raceControl.length}
                  </span>
                )}
              </button>

              <span className="replay-meta">
                {replay.entryCount.toLocaleString()} frames
                {replay.truncated && ' (truncated)'}
              </span>
            </div>

            {replay.truncated && (
              <div className="replay-warning">
                This recording is unusually large, so only the first part was
                loaded. The replay ends early.
              </div>
            )}
            {replay.light && (
              <div className="replay-note">
                Light replay: this device reports little memory, so a team is
                refreshed every 15 seconds instead of on every change. Laps,
                stops and driver changes are all there.
              </div>
            )}
            {replay.raceControl.length === 0 && (
              <div className="replay-note">
                Race control and the countdown were not recorded for events run
                before this feature existed — the grid still replays in full.
              </div>
            )}
          </div>

          <PitWindow teams={replay.teams} rules={replay.raceRules} />

          {isStintPlanVisible && (
            <LiveStrategyPanel
              live={liveStrategy}
              teams={replay.teams}
              eventId={eventId}
              dueSoonMinutes={replay.raceRules.dueSoonMinutes}
            />
          )}

          {isAdmin && isStrategyVisible && (
            <StrategyPanel summary={strategy} ourTeamIds={highlightedTeamIds} />
          )}

          {isAdmin && isChartsVisible && (
            <ChartsPanel
              teams={replay.teams}
              lapHistory={replay.lapHistory}
              ourTeamIds={highlightedTeamIds}
            />
          )}

          {isAdmin && isSectorsVisible && (
            <SectorsPanel
              teams={replay.teams}
              history={replay.sectorHistory}
              version={replay.sectorHistoryVersion}
              ourTeamIds={highlightedTeamIds}
            />
          )}

          {isAdmin && isPlanVisible && (
            <DriverPlanPanel
              teams={replay.teams}
              ourTeamIds={highlightedTeamIds}
              rules={replay.raceRules}
              minutesRemaining={strategy.minutesRemaining}
              plan={replay.racePlan}
              activeEvent={null}
              readOnly
            />
          )}

          <TeamGrid
            teams={replay.teams}
            sortMode={sortMode}
            highlightedTeamIds={highlightedTeamIds}
            onToggleHighlight={toggleHighlight}
            onOpenTeamDetail={setStintsTeamId}
            visibleColumns={getVisibleColumns()}
            searchQuery={searchQuery}
            penaltyCountByKart={penaltyCountByKart}
            onOpenPenalties={() => undefined}
          />

          <TeamStintsModal
            team={stintsTeamId ? replay.teams.get(stintsTeamId) ?? null : null}
            lapHistory={replay.lapHistory}
            onClose={() => setStintsTeamId(null)}
          />

          {isRaceControlVisible && (
            <RaceControlPanel entries={replay.raceControl} />
          )}
        </div>

        <PitSidebar
          teams={replay.teams}
          isVisible={isPitSidebarVisible}
          onToggle={() => setIsPitSidebarVisible((v) => !v)}
          now={() => replay.currentTime}
          storageKey={`karting-pit-assignments:replay:${eventId}`}
        />
      </div>
    </div>
  );
};
